/**
 * Add locked ability to a model
 */
import store from '@/store/store';
import { LOCK, SEATRADIUS, RENDERER_MODE, SEATSTATUS, CHANNEL } from '@/constants';

import SVG from 'svg.js';
import Application from '../application';

export interface LockableRequirements {
  attributes: Record<string, any>;
  view: SVG.G;
  app: Application;
  shape: SVG.Shape;
  hideLabel(): void;
  showLabel(): void;
}

export const LockableMixin = <T extends { new (...args: any[]): LockableRequirements }>(base: T) => {
  return class LockableMixin extends base {
    lockShape: SVG.Path | SVG.Text | null = null;

    get locked() {
      return this.attributes.status === SEATSTATUS.LOCKED;
    }

    set locked(value) {
      this.attributes.status = value ? SEATSTATUS.LOCKED : SEATSTATUS.FREE;
      !value && delete this.attributes.holdType;
      this.toggleLockVisibility();
    }

    hideLock() {
      this.locked && this.lockShape!.hide();
    }

    showLock() {
      this.locked && this.lockShape!.show();
    }

    toggleLockVisibility() {
      if (this.locked) {
        this.hideLabel();
        this.addLockClass();
        this.initLockShape();
      } else {
        if (store.state.seatmap.showLabels) {
          this.showLabel();
        }
        this.removeLockClass();
        this.removeLockShape();
      }
    }

    initLockShape([cx, cy]: number[] = this.attributes.position) {
      if (RENDERER_MODE && this.app.props.channel === CHANNEL.WEB) {
        this.shape.addClass('booked');
      } else {
        if (!this.lockShape || !this.lockShape.node) {
          const size = (SEATRADIUS - 2) * this.app.seatmap.scale;

          const holdType = this.app.props.holdTypes?.find((t) => t.key === this.attributes.holdType);

          if (this.app.props.holdTypes && holdType && (!RENDERER_MODE || this.app.props.channel === CHANNEL.POS)) {
            this.lockShape = this.view
              .text(holdType.key)
              .size(size + 2)
              .center(cx, cy)
              .fill(LOCK.FILL);
          } else {
            this.lockShape = this.view.path(LOCK.SHAPE).size(size).center(cx, cy).fill(LOCK.FILL);
          }
        }
        if (RENDERER_MODE) {
          this.shape.addClass('locked');
          this.lockShape.addClass('lock-shape');

          if (
            this.app.props.allowedHolds?.includes(this.attributes.holdType) &&
            this.app.props.channel === CHANNEL.POS
          ) {
            this.view.addClass('hold-seat');
          }
        }
      }
    }

    removeLockShape() {
      this.lockShape && this.lockShape.remove();
      this.lockShape = null;
    }

    addLockClass() {
      this.view.addClass('locked');
    }

    removeLockClass() {
      this.view.removeClass('locked');
      this.view.removeClass('hold-seat');
    }

    lock(holdType?: string) {
      // this is in case we are locking a seat that is already locked with a different holdType
      // we need to remove the shape. otherwise we end up with 2
      // this is a no-op if the seat wasnt locked
      this.removeLockShape();
      this.removeLockClass();
      if (holdType) {
        this.attributes.holdType = holdType;
      } else {
        delete this.attributes.holdType;
      }
      this.locked = true;
    }

    unlock() {
      delete this.attributes.holdType;
      this.locked = false;
    }

    setLock(flag: boolean) {
      this.locked = flag;
    }

    updateLockShape(x: number, y: number) {
      if (this.lockShape) {
        const size = (SEATRADIUS - 2) * this.app.seatmap.scale;
        this.lockShape.size(size).center(x, y);
      }
    }
  };
};
